import { useState } from 'react'
import type { Origin, Profile, Work } from '../../core/types.ts'
import { api } from '../lib/api.ts'
import { Button } from './ui/button.tsx'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from './ui/dialog.tsx'
import { Input, Label, Select } from './ui/input.tsx'
import { Alert } from './ui/alert.tsx'
import { useToast } from './ui/toast.tsx'

export function EditWorkDialog({
  canonId, work, profile, open, onOpenChange, onSaved,
}: { canonId: string; work: Work; profile: Profile; open: boolean; onOpenChange: (v: boolean) => void; onSaved: (updated: Work) => void }) {
  const [author, setAuthor] = useState(work.author ?? '')
  const [title, setTitle] = useState(work.title)
  const [formId, setFormId] = useState(work.formId)
  const [firstPublished, setFirstPublished] = useState(work.firstPublished?.toString() ?? '')
  const [origin, setOrigin] = useState<Origin>(work.origin)
  const [originalLanguage, setOriginalLanguage] = useState(work.originalLanguage ?? '')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const toast = useToast()

  async function save(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const updated = await api.updateWork(canonId, work.id, {
        author: author.trim() || null,
        title: title.trim(),
        formId,
        firstPublished: firstPublished.trim() ? Number(firstPublished) : null,
        origin,
        originalLanguage: originalLanguage.trim() || null,
      })
      onOpenChange(false)
      onSaved(updated)
      toast('Dílo upraveno.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Uložení se nepodařilo')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Upravit dílo</DialogTitle>
        <DialogDescription>Oprava faktů o díle — projeví se ve všech seznamech z tohoto kánonu.</DialogDescription>

        <form onSubmit={save} className="mt-4 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="work-title">Název</Label>
            <Input id="work-title" value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={200} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="work-author">Autor</Label>
            <Input id="work-author" value={author} onChange={(e) => setAuthor(e.target.value)} placeholder="Prázdné = bez autora (antologie, anonym)" maxLength={120} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="work-form">Forma</Label>
              <Select id="work-form" value={formId} onChange={(e) => setFormId(e.target.value)} required>
                {profile.forms.map((f) => (
                  <option key={f.id} value={f.id}>{f.label}</option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="work-year">Rok prvního vydání</Label>
              <Input
                id="work-year"
                type="number"
                value={firstPublished}
                onChange={(e) => setFirstPublished(e.target.value)}
                placeholder="Neznámý"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="work-origin">Původ</Label>
              <Select id="work-origin" value={origin} onChange={(e) => setOrigin(e.target.value as Origin)} required>
                <option value="cs">Česká</option>
                <option value="world">Světová</option>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="work-lang">Jazyk originálu</Label>
              <Input id="work-lang" value={originalLanguage} onChange={(e) => setOriginalLanguage(e.target.value)} placeholder="Neznámý" maxLength={40} />
            </div>
          </div>

          {error && <Alert variant="destructive">{error}</Alert>}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Zrušit</Button>
            <Button type="submit" disabled={busy}>{busy ? 'Ukládám…' : 'Uložit'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
